import { ApplicationElement, html } from '../application_element/element.js';
import { csrfFetch, uuid } from '../../lib/helpers.js';
import Snack from '@effectivastudio/snackbar';
import { classMap } from 'lit/directives/class-map.js';
import '../avatar/element.js';
import '../icon/element.js';
import translations from './translations.js';

/**
 * The comment form in footer of each card
 *
 * @prop cardId - Id of a card
 * @prop parentId - Id of a parent comment for a reply
 * @prop authorName - For avatar (optional)
 * @prop authorAvatar - For avatar (optional)
 * @prop shouldRenderTrix - Internal state (currently not used)
 */
export default class CommentForm extends ApplicationElement {
  /** @internal */
  static translations = translations;

  static properties = {
    cardId: { type: Number },
    parentId: { type: Number },
    mentionsUrl: { type: String },
    authorName: { type: String },
    authorAvatar: { type: String },
    shouldRenderTrix: { type: Boolean, state: true },
    active: { state: true },
  };

  constructor() {
    super();
    this.trixId = uuid();
    this.shouldRenderTrix = true; // Delete this when there is time to experiment with fakeTrix change
  }

  createRenderRoot() {
    return this;
  }

  get formAction() {
    return `/cards/${this.cardId}/comments`;
  }

  get isReply() {
    return !!this.parentId;
  }

  get submitButtonLabel() {
    return this.t(this.isReply ? 'reply' : 'comment');
  }

  get inputPlaceholderLabel() {
    return this.t(`add_a_${this.isReply ? 'reply' : 'comment'}`);
  }

  fetch({ href, method, body }) {
    this.dispatchEvent(
      new Event('ajax:beforeSend', { bubbles: true, composed: true })
    );
    return csrfFetch(href, { method, body })
      .then(res => this.onFetchSuccess(res))
      .catch(res => this.onFetchError(res));
  }

  onFetchError() {
    this.dispatchEvent(
      new Event('ajax:error', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new Event('ajax:complete', { bubbles: true, composed: true })
    );
    this.displaySnackBarError();
  }

  onFetchSuccess() {
    this.dispatchEvent(
      new Event('ajax:success', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new Event('ajax:complete', { bubbles: true, composed: true })
    );
    this.form.reset();
  }

  get form() {
    return this.querySelector('form');
  }

  get editor() {
    return this.querySelector('trix-editor');
  }

  get formData() {
    return new FormData(this.form);
  }

  isValid(formData = this.formData) {
    const body = formData.get('card_comment[body]');
    const bodyPlain =
      new DOMParser().parseFromString(body, 'text/html').body.textContent || '';
    if (bodyPlain.trim() === '') {
      return false;
    }

    return true;
  }

  onSubmit(e) {
    e.preventDefault();
    const data = new FormData(this.form);

    if (!this.isValid(data)) {
      return;
    }

    this.form.reset();

    this.fetch({
      href: this.form.action,
      method: this.form.method || 'GET',
      body: data,
    });
  }

  onFormReset() {
    this.editor.focus();
  }

  // // Uncomment this when there is time to experiment with fakeTrix change
  // updated(changedProperties) {
  //   if (changedProperties.has('shouldRenderTrix')) {
  //     this.shouldRenderTrix && this.editor.focus();
  //   }
  // }

  displaySnackBarError() {
    Snack({
      text: this.t('snack.generic_error_msg'),
      customClass: 'snackbar-danger',
    });
  }

  revealTrix() {
    this.shouldRenderTrix = true;
  }

  onTrixChange(e) {
    this.active = !!e.target.value.trim();
  }

  renderFakeTrix() {
    return html`
      <input
        @focus=${this.revealTrix}
        class="form-control"
        placeholder=${this.inputPlaceholderLabel} />
    `;
  }

  renderTrix() {
    return html`
      <bu-rte
        layout="mini"
        submitOnEnter=${!window.AppConfig?.runtime?.isMobileDevice}
        tributeRemoteUrl=${this.mentionsUrl}
        @trix-change=${this.onTrixChange}>
        <input
          class="form-control hidden"
          id="body-${this.trixId}"
          type="hidden"
          name="card_comment[body]" />
        <trix-editor
          input="body-${this.trixId}"
          placeholder=${this.inputPlaceholderLabel}></trix-editor>
      </bu-rte>
    `;
  }

  render() {
    return html`
      <ef-avatar
        rounded
        layout="xs"
        name=${this.authorName}
        picture=${this.authorAvatar}></ef-avatar>

      <form
        @reset=${this.onFormReset}
        @submit=${this.onSubmit}
        class=${classMap({
          simple_form: true,
          'form-default': true,
          'card-comment-form': true,
          active: this.active,
        })}
        autocomplete="off"
        action=${this.formAction}
        accept-charset="UTF-8"
        novalidate
        method="POST">
        ${this.parentId &&
        html`<input
          class="form-control"
          type="hidden"
          name="card_comment[parent_id]"
          value=${this.parentId} />`}
        ${this.shouldRenderTrix ? this.renderTrix() : this.renderFakeTrix()}

        <div class="actions">
          <button class="btn btn-v1 t7 btn-sm" type="reset">
            <ef-icon name="close"></ef-icon>
            <span>${this.t('cancel')}</span>
          </button>
          <button type="submit" class="btn btn-v1 t1 btn-sm">
            <ef-icon name="comment-lines"></ef-icon>
            <span>${this.submitButtonLabel}</span>
          </button>
        </div>
      </form>
    `;
  }
}

CommentForm.registerTagName('ef-comment-form');

customElements.get('ef-comment-form') ||
  customElements.define('ef-comment-form', CommentForm);
